import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  TrendingUp,
  Users,
  ShoppingCart,
  DollarSign,
  Activity,
  Bell,
  ArrowUpRight,
  User,
  CreditCard,
  Package,
  Trophy,
  ArrowRightLeft,
  Headphones,
} from "lucide-react";
import { useSocket } from "../../services/GlobalSocket";
import { useDashboardData } from "../../hooks/useDashboardData";
import UserOverviewSection from "./UserOverviewSection";
import { useNavigate } from "react-router-dom";

const Dashboard = React.memo(() => {
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isVisible, setIsVisible] = useState(false);

  // Use socket context and dashboard data hook
  const socket = useSocket();
  const { dashboardData, loading, error } = useDashboardData();

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);

    // Add smooth entrance animation
    setTimeout(() => setIsVisible(true), 100);

    return () => {
      clearInterval(timer);
    };
  }, []);

  // Format duration to human readable - memoized
  const formatDuration = useCallback((ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ago`;
    } else if (minutes > 0) {
      return `${minutes}m ago`;
    } else {
      return `${seconds}s ago`;
    }
  }, []);

  // Custom tooltip for revenue chart - memoized
  const CustomTooltip = useCallback(({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="chart-tooltip">
          <p className="tooltip-label">{`${label}`}</p>
          <p className="tooltip-value">
            {`Revenue: $${(payload[0].value / 100).toLocaleString()}`}
          </p>
        </div>
      );
    }
    return null;
  }, []);

  // Memoized StatCard component
  const StatCard = useMemo(
    () =>
      React.memo(({ icon: Icon, title, value, subtitle, trend, color }) => (
        <div className="stat-card">
          <div className="stat-header">
            <div className="stat-icon" style={{ backgroundColor: color }}>
              <Icon size={20} color="white" />
            </div>
            <div className="stat-trend">
              <TrendingUp size={14} />
              <span>{Math.abs(trend)}%</span>
            </div>
          </div>
          <div className="stat-content">
            <h4>{title}</h4>
            <div className="stat-value">{value}</div>
            <div className="stat-subtitle">{subtitle}</div>
          </div>
        </div>
      )),
    []
  );

  // Memoized online users list
  const onlineUsersList = useMemo(
    () =>
      dashboardData.serverStatus.onlineUsers.users
        .slice(0, 5)
        .map((user, index) => (
          <div key={user.id || index} className="online-user-item">
            <div className="user-avatar">
              <User size={16} />
            </div>
            <div className="user-info">
              <p>{user.name || user.email || `User ${index + 1}`}</p>
              <span>
                Connected{" "}
                {user.duration ? formatDuration(user.duration) : "now"}
              </span>
            </div>
            <div className="user-status online">●</div>
          </div>
        )),
    [dashboardData.serverStatus.onlineUsers.users, formatDuration]
  );

  if (loading) {
    return (
      <div className="dashboard loading">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading dashboard data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`dashboard ${isVisible ? "visible" : ""}`}>
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-left">
          <Activity size={28} />
          <div>
            <h1>Analytics Dashboard</h1>
            <p>Welcome back! Here's what's happening today.</p>
          </div>
        </div>
        <div className="header-actions">
          {/* Removed connection status section */}
          <div className="header-time">{currentTime.toLocaleTimeString()}</div>
        </div>
      </div>
      {/* Error Banner */}
      {error && (
        <div className="error-banner">
          <p>⚠️ Error loading live data: {error}. Showing fallback data.</p>
        </div>
      )}
      {/* Socket Status Banner */}
      {socket.error && (
        <div className="socket-error-banner">
          <p>🔌 Socket Error: {socket.error}</p>
        </div>
      )}
      <UserOverviewSection
        compact
        onSegmentChange={(segment) => navigate(`/UserManagement?segment=${segment}`)}
      />
      <div className="stats-grid">
        <StatCard
          icon={DollarSign}
          title="Total Revenue"
          value={dashboardData.overview.totalRevenue.formatted}
          subtitle={dashboardData.overview.totalRevenue.period}
          trend={dashboardData.overview.totalRevenue.change}
          color="#10b981"
        />
        <StatCard
          icon={ShoppingCart}
          title="Orders"
          value={dashboardData.overview.orders.formatted}
          subtitle={dashboardData.overview.orders.period}
          trend={dashboardData.overview.orders.change}
          color="#6366f1"
        />
        <StatCard
          icon={Users}
          title="Active Users"
          value={dashboardData.overview.activeUsers.formatted}
          subtitle={dashboardData.overview.activeUsers.period}
          trend={dashboardData.overview.activeUsers.change}
          color="#8b5cf6"
        />
      </div>
      {/* Charts */}
      <div className="charts-grid">
        <div className="chart-card">
          <h3>Revenue Trend</h3>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart
              data={dashboardData.revenueTrend}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 12 }}
                axisLine={{ stroke: "#e0e0e0" }}
              />
              <YAxis
                tick={{ fontSize: 12 }}
                axisLine={{ stroke: "#e0e0e0" }}
                tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                dataKey="revenue"
                stroke="#10b981"
                strokeWidth={3}
                dot={{ fill: "#10b981", strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: "#10b981", strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card">
          <h3>Server Status</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie
                data={dashboardData.trafficSources}
                dataKey="value"
                nameKey="source"
                cx="50%"
                cy="50%"
                outerRadius={90}
                fill="#8884d8"
              >
                {dashboardData.trafficSources.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="legend">
            {dashboardData.trafficSources.map((item, index) => (
              <div key={index} className="legend-item">
                <div
                  className="legend-color"
                  style={{ backgroundColor: item.color }}
                />
                <span>
                  {item.source}: {item.value}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Bottom Grid */}
      <div className="bottom-grid">
        <div className="online-users-card">
          <div className="card-header">
            <Users size={20} />
            <h3>
              Online Users ({dashboardData.serverStatus.onlineUsers.count})
            </h3>
          </div>
          <div className="online-users-list">
            {onlineUsersList}
            {dashboardData.serverStatus.onlineUsers.count > 5 && (
              <div className="more-users">
                +{dashboardData.serverStatus.onlineUsers.count - 5} more users
                online
              </div>
            )}
          </div>
        </div>

        <div className="quick-stats-card">
          <div className="card-header">
            <Activity size={20} />
            <h3>Quick Stats</h3>
          </div>
          <div className="quick-stats-grid">
            <div className="quick-stat">
              <span className="stat-label">Avg. Order Value</span>
              <span className="stat-number">
                {dashboardData.quickStats.avgOrderValue.formatted}
              </span>
            </div>
            <div className="quick-stat">
              <span className="stat-label">Conversion Rate</span>
              <span className="stat-number">
                {dashboardData.quickStats.conversionRate.formatted}
              </span>
            </div>
            <div className="quick-stat">
              <span className="stat-label">Bounce Rate</span>
              <span className="stat-number">
                {dashboardData.quickStats.bounceRate.formatted}
              </span>
            </div>
            <div className="quick-stat">
              <span className="stat-label">Active Sessions</span>
              <span className="stat-number">
                {dashboardData.quickStats.activeSessions.formatted}
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Enhanced CSS */}
      <style>{`
        .dashboard {
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
            sans-serif;
          opacity: 0;
          transform: translateY(10px);
          transition: all 0.3s ease-out;
        }

        .dashboard.visible {
          opacity: 1;
          transform: translateY(0);
        }

        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
          padding: 0 10px;
          background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
          border-radius: 16px;
          padding: 20px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .header-left h1 {
          margin: 0;
          color: #1f2937;
          font-size: 28px;
          font-weight: 700;
        }

        .header-left p {
          margin: 0;
          color: #6b7280;
          font-size: 16px;
        }

        .header-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .header-time {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          padding: 12px 20px;
          border-radius: 16px;
          font-weight: 600;
          font-size: 14px;
          box-shadow: 0 4px 6px -1px rgba(102, 126, 234, 0.25);
        }

        .pulse {
          animation: pulse 1.5s ease-in-out infinite;
        }

        @keyframes pulse {
          0% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.7;
            transform: scale(1.1);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }

        .error-banner {
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          padding: 12px;
          margin-bottom: 20px;
          color: #dc2626;
          animation: slideIn 0.3s ease-out;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .loading {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 400px;
          opacity: 1;
          transform: none;
        }

        .loading-container {
          text-align: center;
        }

        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid #f3f4f6;
          border-top: 3px solid #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto 16px;
        }

        @keyframes spin {
          0% {
            transform: rotate(0deg);
          }
          100% {
            transform: rotate(360deg);
          }
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .stat-card {
          background: white;
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          border: 1px solid #f3f4f6;
          transition: all 0.3s ease;
        }

        .stat-card:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 20px -5px rgba(0, 0, 0, 0.1);
        }

        .stat-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .stat-trend {
          display: flex;
          align-items: center;
          gap: 4px;
          color: #10b981;
          font-weight: 600;
          font-size: 14px;
        }

        .stat-content h4 {
          margin: 0 0 8px 0;
          color: #6b7280;
          font-size: 14px;
          font-weight: 500;
        }

        .stat-value {
          font-size: 32px;
          font-weight: 700;
          color: #1f2937;
          margin-bottom: 4px;
        }

        .stat-subtitle {
          color: #6b7280;
          font-size: 14px;
        }

        .charts-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 20px;
          margin-bottom: 30px;
        }

        .chart-card {
          background: white;
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          border: 1px solid #f3f4f6;
          transition: all 0.3s ease;
          min-height: 360px;
        }

        .chart-card:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 20px -5px rgba(0, 0, 0, 0.1);
        }

        .chart-card h3 {
          margin: 0 0 20px 0;
          color: #1f2937;
          font-size: 18px;
          font-weight: 600;
        }

        .chart-tooltip {
          background: white;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 12px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        }

        .tooltip-label {
          margin: 0 0 4px 0;
          font-weight: 600;
          color: #1f2937;
        }

        .tooltip-value {
          margin: 0;
          color: #10b981;
          font-weight: 500;
        }

        .legend {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 16px;
        }

        .legend-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
          color: #6b7280;
        }

        .legend-color {
          width: 12px;
          height: 12px;
          border-radius: 2px;
        }

        .bottom-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 20px;
        }

        .quick-stats-card,
        .online-users-card {
          background: white;
          border-radius: 16px;
          padding: 24px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
          border: 1px solid #f3f4f6;
          transition: all 0.3s ease;
        }

        .quick-stats-card:hover,
        .online-users-card:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 20px -5px rgba(0, 0, 0, 0.1);
        }

        .card-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 20px;
        }

        .card-header h3 {
          margin: 0;
          color: #1f2937;
          font-size: 18px;
          font-weight: 600;
        }

        .server-summary {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 12px;
          margin-bottom: 20px;
        }

        .server-metric {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: 12px;
          background: #f9fafb;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .server-metric:hover {
          background: #f3f4f6;
        }

        .metric-label {
          color: #6b7280;
          font-size: 12px;
          margin-bottom: 4px;
        }

        .metric-value {
          color: #1f2937;
          font-size: 16px;
          font-weight: 700;
        }

        .online-users-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .online-user-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 8px 12px;
          background: #f9fafb;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .online-user-item:hover {
          background: #f3f4f6;
        }

        .user-avatar {
          width: 32px;
          height: 32px;
          background: #e5e7eb;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #6b7280;
        }

        .user-info {
          flex: 1;
        }

        .user-info p {
          margin: 0 0 2px 0;
          color: #1f2937;
          font-weight: 500;
          font-size: 14px;
        }

        .user-info span {
          color: #6b7280;
          font-size: 12px;
        }

        .user-status {
          font-size: 12px;
          font-weight: 700;
        }

        .user-status.online {
          color: #10b981;
        }

        .more-users {
          text-align: center;
          color: #6b7280;
          font-size: 14px;
          padding: 8px;
          background: #f9fafb;
          border-radius: 8px;
          margin-top: 8px;
        }

        .quick-stats-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }

        .quick-stat {
          text-align: center;
          padding: 16px;
          background: #f9fafb;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .quick-stat:hover {
          background: #f3f4f6;
        }

        .stat-label {
          display: block;
          color: #6b7280;
          font-size: 14px;
          margin-bottom: 8px;
        }

        .stat-number {
          display: block;
          color: #1f2937;
          font-size: 24px;
          font-weight: 700;
        }

        .connection-status {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #f3f4f6;
          border-radius: 20px;
          padding: 8px 12px;
          font-size: 14px;
          font-weight: 600;
          color: #1f2937;
        }

        .connection-status .connected {
          color: #10b981;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .connection-status .disconnected {
          color: #ef4444;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .last-update {
          font-size: 14px;
          color: #6b7280;
        }

        .reconnect-info {
          font-size: 14px;
          color: #6b7280;
          margin-left: 10px;
        }

        .connection-status .connecting {
          color: #f59e0b;
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .connection-status .connecting .spin {
          animation: spin 1s linear infinite;
        }

        .socket-error-banner {
          background: #fef3c7;
          border: 1px solid #fcd34d;
          border-radius: 8px;
          padding: 12px;
          margin-bottom: 20px;
          color: #d97706;
          animation: slideIn 0.3s ease-out;
        }

        @media (max-width: 768px) {
          .dashboard {
            padding: 15px;
          }

          .dashboard-header {
            flex-direction: column;
            gap: 20px;
            align-items: flex-start;
          }

          .header-actions {
            width: 100%;
            justify-content: flex-end;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .charts-grid {
            grid-template-columns: 1fr;
          }

          .bottom-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
});

export default Dashboard;
